import React, { useState, useEffect, useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { AppPageHeader } from '../components/app/AppPage';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  Container,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  Calculate,
  LocationOn,
  Business,
  ExpandMore,
  Info,
  CheckCircle,
  Warning,
  AccountBalance,
  Category,
  Receipt,
  TrendingUp,
  Refresh
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { addressService } from '../services/addressService';
import taxService from '../services/taxService';
import { companyService } from '../services/companyService';

const US_STATES = [
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'DC', label: 'District of Columbia' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' }
];

// Product tax codes supported by the GoTruTax engine
const TAX_CODES = [
  { value: 'txcd_10000000', label: 'General - Electronically Supplied Services', description: 'Digital services, software, downloads' },
  { value: 'txcd_99999999', label: 'General - Tangible Goods', description: 'Physical products that can be touched/moved' },
  { value: 'txcd_20030000', label: 'General - Services', description: 'Professional and general services' },
  { value: 'txcd_00000000', label: 'Nontaxable', description: 'Tax-exempt goods or services' },
  { value: 'txcd_10103000', label: 'Software as a Service (SaaS) - Personal', description: 'Cloud software for personal use' },
  { value: 'txcd_10103001', label: 'Software as a Service (SaaS) - Business', description: 'Cloud software for business use' },
  { value: 'txcd_10202000', label: 'Downloadable Software - Personal', description: 'Software downloads for personal use' },
  { value: 'txcd_10202003', label: 'Downloadable Software - Business', description: 'Software downloads for business use' },
  { value: 'txcd_92010001', label: 'Shipping', description: 'Shipping and delivery charges' },
];

const isProduction = process.env.NODE_ENV === 'production';
const debugLog = (...args: any[]) => {
  if (!isProduction) {
    console.log(...args);
  }
};
const debugWarn = (...args: any[]) => {
  if (!isProduction) {
    console.warn(...args);
  }
};

const TaxCalculatorPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState<any[]>([]);
  const [registrations, setRegistrations] = useState<any[]>([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [amount, setAmount] = useState('');
  const [taxCode, setTaxCode] = useState('txcd_99999999');
  const [address, setAddress] = useState({
    line1: '',
    city: '',
    state: 'CA',
    postal_code: '',
    country: 'US'
  });
  const [calculation, setCalculation] = useState<any>(null);
  const [error, setError] = useState('');

  // Load companies and registrations
  const loadData = useCallback(async () => {
    try {
      const companiesResponse = await companyService.getCompanies();
      setCompanies(companiesResponse || []);
      
      debugLog('Tax Calculator - Loaded companies:', companiesResponse?.length || 0);
      
      // Auto-select company if there's only one
      if (companiesResponse && companiesResponse.length === 1) {
        const singleCompany = companiesResponse[0];
        setSelectedCompany(singleCompany.id.toString());
        
        // Load registrations for the auto-selected company
        try {
          const params = { company_id: singleCompany.id };
          const registrationsResponse = await (taxService as any).getTaxRegistrations(params);
          setRegistrations(registrationsResponse || []);
          debugLog('Tax Calculator - Auto-selected company and loaded registrations:', singleCompany.name);
        } catch (error) {
          console.error('Failed to load registrations for auto-selected company', error);
          setRegistrations([]);
        }
      }
      
    } catch (error) {
      console.error('Failed to load data', error);
      enqueueSnackbar('Failed to load companies', { variant: 'warning' });
      // Set empty arrays on error
      setCompanies([]);
      setRegistrations([]);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCalculate = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    if (!address.city || !address.state || !address.postal_code) {
      setError('Please fill in all address fields');
      return;
    }

    const inputAmount = parseFloat(amount);
    
    if (inputAmount > 99999999) {
      setError('Amount is too large. Please enter an amount under $99,999,999');
      return;
    }

    if (address.country === 'US' && !/^\d{5}(-\d{4})?$/.test(address.postal_code)) {
      setError('Please enter a valid US ZIP code (e.g., 12345 or 12345-6789)');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const amountInCents = Math.round(inputAmount * 100);
      
      const calculationData = {
        currency: "usd",
        customer_details: {
          address: {
            line1: address.line1 || "123 Main St",
            city: address.city,
            state: address.state,
            postal_code: address.postal_code,
            country: address.country
          },
          address_source: "shipping"
        },
        line_items: [{
          amount: amountInCents,
          quantity: 1,
          reference: "Product",
          tax_code: taxCode,
          tax_behavior: "exclusive"
        }],
        company_id: selectedCompany ? parseInt(selectedCompany) : undefined
      };

      const result = await taxService.calculateTax(calculationData);
      
      const transformedResult = {
        ...result,
        subtotal: inputAmount,
        taxAmount: result.taxAmount || 0,
        total: inputAmount + (result.taxAmount || 0),
        effectiveRate: result.taxAmount ? (result.taxAmount / inputAmount) : 0,
        jurisdictions: result.jurisdictions || [],
        engine: 'gotrutax'
      };
      
      setCalculation(transformedResult);
      
    } catch (error: any) {
      console.error('Tax calculation failed:', error);
      setError(error.response?.data?.detail || error.message || 'Tax calculation failed');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value);
  };

  const formatPercentage = (value: number) => {
    return `${(value * 100).toFixed(2)}%`;
  };

  const getRegistrationsForState = (state: string) => {
    // Ensure registrations is an array
    if (!Array.isArray(registrations)) {
      debugWarn('Registrations is not an array:', registrations);
      return [];
    }
    
    if (!selectedCompany) {
      // If no company selected, show all active registrations for the state
      return registrations.filter((reg: any) => 
        reg.state === state && reg.status === 'active'
      );
    }
    
    // Filter for the selected company and state
    return registrations.filter((reg: any) => {
      const isCorrectState = reg.state === state;
      const isActive = reg.status === 'active';
      const isCorrectCompany = reg.company && reg.company.id === parseInt(selectedCompany);
      
      debugLog('Registration filter debug:', {
        reg,
        isCorrectState,
        isActive,
        isCorrectCompany,
        selectedCompany: parseInt(selectedCompany),
        regCompanyId: reg.company?.id
      });
      
      return isCorrectState && isActive && isCorrectCompany;
    });
  };

  const getSelectedCompanyInfo = () => {
    if (!selectedCompany) return null;
    return companies.find((c: any) => c.id === parseInt(selectedCompany));
  };

  const getRegistrationsForAllStates = () => {
    // Ensure registrations is an array
    if (!Array.isArray(registrations)) {
      debugWarn('Registrations is not an array:', registrations);
      return [];
    }
    
    if (!selectedCompany) {
      // If no company selected, show all active registrations
      return registrations.filter((reg: any) => reg.status === 'active');
    }
    
    // Filter for the selected company across all states
    return registrations.filter((reg: any) => {
      const isActive = reg.status === 'active';
      const isCorrectCompany = reg.company && reg.company.id === parseInt(selectedCompany);
      
      debugLog('All states registration filter debug:', {
        reg,
        isActive,
        isCorrectCompany,
        selectedCompany
      });
      
      return isActive && isCorrectCompany;
    });
  };

  // Generate real address for testing
  const generateRandomAddress = async () => {
    try {
      const response = await addressService.generateRandomRealAddress();
      
      if (response.success) {
        setAddress({
          line1: response.address.line1,
          city: response.address.city,
          state: response.address.state,
          postal_code: response.address.postal_code,
          country: response.address.country
        });
        
        enqueueSnackbar(`Real address generated for ${response.state_used} (${response.source})`, { variant: 'success' });
      } else {
        // Use fallback address if API fails
    setAddress({
          line1: response.address.line1,
          city: response.address.city,
          state: response.address.state,
          postal_code: response.address.postal_code,
          country: response.address.country
    });
    
        enqueueSnackbar('Fallback address generated (API unavailable)', { variant: 'warning' });
      }
    } catch (error) {
      console.error('Error generating real address:', error);
      enqueueSnackbar('Failed to generate real address', { variant: 'error' });
    }
  };

  return (
    <Container maxWidth="xl" className="gt-page gt-page-wide">
      <AppPageHeader
        title="Tax calculator"
        description="Calculate sales tax for one sale by address and product category, across every jurisdiction that applies."
        actions={<Button variant="outlined" component={RouterLink} to="/developers/console">Calculate from your checkout</Button>}
      />

      <Grid container spacing={3}>
        {/* Tax Factors Information */}
        <Grid item xs={12}>
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Info sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Factors That Impact Tax Calculation
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <List dense>
                    <ListItem>
                      <ListItemIcon><LocationOn color="primary" /></ListItemIcon>
                      <ListItemText 
                        primary="Customer Location (Ship-to Address)"
                        secondary="State, city, and ZIP code determine local tax rates"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><Business color="primary" /></ListItemIcon>
                      <ListItemText 
                        primary="Seller Location (Ship-from Address)"
                        secondary="Your business address affects nexus and origin-based taxes"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><Category color="primary" /></ListItemIcon>
                      <ListItemText 
                        primary="Product Tax Code"
                        secondary="Different products have different tax rates (e.g., clothing vs. digital services)"
                      />
                    </ListItem>
                  </List>
                </Grid>
                <Grid item xs={12} md={6}>
                  <List dense>
                    <ListItem>
                      <ListItemIcon><AccountBalance color="primary" /></ListItemIcon>
                      <ListItemText 
                        primary="Customer Status"
                        secondary="Business vs. individual, tax-exempt status, VAT registration"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><Receipt color="primary" /></ListItemIcon>
                      <ListItemText 
                        primary="Tax Behavior"
                        secondary="Whether tax is included in price (inclusive) or added on top (exclusive)"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><TrendingUp color="primary" /></ListItemIcon>
                      <ListItemText 
                        primary="Transaction Amount"
                        secondary="Some jurisdictions have thresholds or different rates for different amounts"
                      />
                    </ListItem>
                  </List>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Grid>

        {/* Company Registration Status */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                Tax Registration Status
              </Typography>
              
              {selectedCompany && getSelectedCompanyInfo() ? (
                <Box>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="subtitle2">
                      Selected Company: {getSelectedCompanyInfo().name}
                    </Typography>
                  </Alert>
                  
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Active Sales Tax Registrations:
                    </Typography>
                    {getRegistrationsForAllStates().length > 0 ? (
                      <Grid container spacing={1}>
                        {getRegistrationsForAllStates().map((reg, index) => (
                          <Grid item key={index}>
                            <Chip
                              icon={<CheckCircle />}
                              label={`${reg.state}: ${reg.registration_type ? 
                                reg.registration_type.split('_').map(
                                  (word: string) => word.charAt(0).toUpperCase() + word.slice(1)
                                ).join(' ') : 'Registration'}`}
                              color="success"
                              variant="outlined"
                              size="small"
                            />
                          </Grid>
                        ))}
                      </Grid>
                    ) : (
                      <Chip
                        icon={<Warning />}
                        label="No active registrations found"
                        color="warning"
                        variant="outlined"
                        size="small"
                      />
                    )}
                  </Box>
                </Box>
              ) : (
                <Box>
                  <Typography variant="subtitle2" gutterBottom sx={{ mb: 2 }}>
                    Company Selection
                  </Typography>
                  
                  {companies.length === 0 ? (
                    <Alert severity="info">
                      No companies available. Please create a company first.
                    </Alert>
                  ) : companies.length === 1 ? (
                    <Alert severity="info">
                      Using company: {companies[0].name}
                    </Alert>
                  ) : (
                    <FormControl fullWidth>
                      <InputLabel>Select Company</InputLabel>
                      <Select
                        value={selectedCompany}
                        onChange={async (e) => {
                          const newCompany = e.target.value;
                          setSelectedCompany(newCompany);
                          
                          // Load registrations for the new company
                          if (newCompany) {
                            try {
                              const params = { company_id: newCompany };
                              const registrationsResponse = await (taxService as any).getTaxRegistrations(params);
                              setRegistrations(registrationsResponse || []);
                              debugLog('Tax Calculator - Loaded registrations for company:', newCompany);
                            } catch (error) {
                              console.error('Failed to load registrations', error);
                              setRegistrations([]);
                            }
                          } else {
                            setRegistrations([]);
                          }
                        }}
                        label="Select Company"
                      >
                        <MenuItem value="">
                          <em>Select a company to see tax registrations</em>
                        </MenuItem>
                        {companies.map((company) => (
                          <MenuItem key={company.id} value={company.id.toString()}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Business sx={{ mr: 1, fontSize: 16 }} />
                              {company.name}
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Calculator Form */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Calculate Tax
              </Typography>
              
              <Grid container spacing={3}>
                {/* Company Selection */}
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Company (Optional)</InputLabel>
                    <Select
                      value={selectedCompany}
                      onChange={async (e) => {
                        const newCompany = e.target.value;
                        setSelectedCompany(newCompany);
                        
                        // Load registrations for the new company
                        if (newCompany) {
                          try {
                            const params = { company_id: newCompany };
                            const registrationsResponse = await (taxService as any).getTaxRegistrations(params);
                            setRegistrations(registrationsResponse || []);
                            debugLog('Tax Calculator - Loaded registrations for company:', newCompany);
                          } catch (error) {
                            console.error('Failed to load registrations', error);
                            setRegistrations([]);
                          }
                        } else {
                          setRegistrations([]);
                        }
                      }}
                      label="Company (Optional)"
                    >
                      <MenuItem value="">
                        <em>No specific company</em>
                      </MenuItem>
                      {companies.map((company) => (
                        <MenuItem key={company.id} value={company.id.toString()}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Business sx={{ mr: 1, fontSize: 16 }} />
                            {company.name}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Amount and Tax Code */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Amount"
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    inputProps={{ min: 0, step: 0.01 }}
                    InputProps={{
                      startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Product Tax Code</InputLabel>
                    <Select
                      value={taxCode}
                      onChange={(e) => setTaxCode(e.target.value)}
                      label="Product Tax Code"
                    >
                      {TAX_CODES.map((code) => (
                        <MenuItem key={code.value} value={code.value}>
                          <Tooltip title={code.description} placement="right">
                            <Box>
                              <Typography variant="body2">{code.label}</Typography>
                            </Box>
                          </Tooltip>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Address Section */}
                <Grid item xs={12}>
                  <Typography variant="subtitle1" sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    mb: 1,
                    color: 'primary.main'
                  }}>
                    <LocationOn sx={{ mr: 1, fontSize: 18 }} />
                    Customer Address
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Fill in customer address for tax calculation
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={generateRandomAddress}
                      startIcon={<Refresh />}
                      sx={{ minWidth: 'auto' }}
                    >
                      Generate Real Address
                    </Button>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Street Address"
                    value={address.line1}
                    onChange={(e) => setAddress({ ...address, line1: e.target.value })}
                    placeholder="123 Main St (optional)"
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="City"
                    value={address.city}
                    onChange={(e) => setAddress({ ...address, city: e.target.value })}
                    required
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <FormControl fullWidth required>
                    <InputLabel>State</InputLabel>
                    <Select
                      value={address.state}
                      onChange={(e) => setAddress({ ...address, state: e.target.value })}
                      label="State"
                    >
                      {US_STATES.map((state) => (
                        <MenuItem key={state.value} value={state.value}>
                          {state.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="ZIP Code"
                    value={address.postal_code}
                    onChange={(e) => setAddress({ ...address, postal_code: e.target.value })}
                    required
                  />
                </Grid>

                {error && (
                  <Grid item xs={12}>
                    <Alert severity="error">{error}</Alert>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Button 
                    variant="contained" 
                    size="large"
                    onClick={handleCalculate} 
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} /> : <Calculate />}
                    sx={{ mt: 2 }}
                  >
                    {loading ? 'Calculating...' : 'Calculate Tax'}
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Results */}
        {calculation && (
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom color="primary" sx={{ fontWeight: 600 }}>
                  Tax Calculation Results
                </Typography>
                
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Subtotal
                    </Typography>
                    <Typography variant="h6">
                      {formatCurrency(calculation.subtotal)}
                    </Typography>
                  </Box>
                  
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Tax Amount
                    </Typography>
                    <Typography variant="h6" color="primary">
                      {formatCurrency(calculation.taxAmount)}
                    </Typography>
                  </Box>
                  
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Total Amount
                    </Typography>
                    <Typography variant="h5" fontWeight="bold">
                      {formatCurrency(calculation.total)}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Effective Tax Rate
                    </Typography>
                    <Typography variant="h6">
                      {formatPercentage(calculation.effectiveRate)}
                    </Typography>
                  </Box>

                  {calculation.jurisdictions && calculation.jurisdictions.length > 0 && (
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Tax Jurisdictions
                      </Typography>
                      {calculation.jurisdictions.map((jurisdiction: any, index: number) => (
                        <Box key={index} sx={{ mb: 1 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Chip 
                              label={`${jurisdiction.name} (${jurisdiction.type})`}
                              size="small"
                              variant="outlined"
                            />
                            <Typography variant="body2">
                              {formatPercentage(jurisdiction.rate)} - {formatCurrency(jurisdiction.taxAmount)}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}

                  {calculation.engine && (
                    <Box sx={{ mt: 2 }}>
                      <Chip 
                        label="Calculated by GoTruTax"
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </Box>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Container>
  );
};

export default TaxCalculatorPage; 